import { SetStateAction, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Grid, Card, CardContent, CardActionArea,
  Container, Chip, Avatar, Button, useTheme, alpha, Paper,
  IconButton, Divider, Tabs, Tab, TextField, MenuItem, FormControl,
  Select, InputLabel, InputBase, InputAdornment, CircularProgress
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  MenuOpen as MenuOpenIcon,
  Close as CloseIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  Business as BusinessIcon,
  LocationOn as LocationIcon,
  Person as PersonIcon,
  ArrowForward as ArrowForwardIcon,
  PublishedWithChanges as PublishedIcon,
  Storefront as StoreIcon,
  Handshake as HandshakeIcon,
  AccountBalance as InvestorIcon,
  PriceCheck as FundingIcon
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import { sectors, countries } from '../data/marketplaceData';
import * as marketplaceApi from '../services/marketplace';
import * as homeContentApi from '../services/homeContent';
import Sidebar from '../components/common/Sidebar';

const MotionCard = motion(Card);
const MotionPaper = motion(Paper);
const MotionBox = motion(Box);

const drawerWidth = 240;

const MainContent = styled(Box)<{ isSidebarOpen: boolean }>(({ theme, isSidebarOpen }) => ({
  flexGrow: 1,
  transition: theme.transitions.create('margin', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  marginLeft: isSidebarOpen ? drawerWidth : 0,
  width: '100%'
}));

const SectionHeader = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(4),
  position: 'relative',
  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: -8,
    left: 0,
    width: 60,
    height: 4,
    borderRadius: 2,
    background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
  }
}));

const SearchBar = styled(Paper)(({ theme }) => ({
  padding: '12px 20px',
  display: 'flex',
  alignItems: 'center',
  width: '100%',
  borderRadius: theme.spacing(3),
  marginBottom: theme.spacing(3),
  background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)}, ${alpha(theme.palette.secondary.main, 0.05)})`,
  border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
  backdropFilter: 'blur(10px)',
  boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.1)}`,
  transition: 'all 0.3s ease',
  '&:hover': {
    background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.12)}, ${alpha(theme.palette.secondary.main, 0.08)})`,
    borderColor: alpha(theme.palette.primary.main, 0.3),
    boxShadow: `0 6px 24px ${alpha(theme.palette.primary.main, 0.15)}, 0 0 0 3px ${alpha(theme.palette.primary.main, 0.05)}`
  }
}));

const MarketplacePage = () => {
  const navigate = useNavigate();

  const [tabValue, setTabValue] = useState(0);
  const [sectorFilter, setSectorFilter] = useState("All Sectors");
  const [countryFilter, setCountryFilter] = useState("All Countries");
  const [searchQuery, setSearchQuery] = useState("");
  const [projects, setProjects] = useState<marketplaceApi.MarketplaceProject[]>([]);
  const [investorRequests, setInvestorRequests] = useState<marketplaceApi.InvestorRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [featuredTestimonials, setFeaturedTestimonials] = useState<any[]>([]);
  const theme = useTheme();

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [projectsData, requestsData, homeContentData] = await Promise.all([
        marketplaceApi.getAllProjects(),
        marketplaceApi.getAllInvestorRequests(),
        homeContentApi.getHomeContent()
      ]);
      setProjects(projectsData);
      setInvestorRequests(requestsData);

      // Load featured testimonials from home content
      if (homeContentData?.content?.featured_testimonials) {
        setFeaturedTestimonials(homeContentData.content.featured_testimonials.map((item: any) => item.resource));
      }
    } catch (error) {
      console.error('Error loading marketplace data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (_event: any, newValue: SetStateAction<number>) => {
    setTabValue(newValue);
  };

  const handleOpportunityClick = (id: string) => {
    navigate(`/marketplace/opportunity/${id}`);
  };

  const handleRequestClick = (id: string) => {
    navigate(`/marketplace/request/${id}`);
  };

  // Filter function for opportunities
  const filteredOpportunities = projects.filter(item => {
    return (
      (sectorFilter === "All Sectors" || item.sector.includes(sectorFilter)) &&
      (countryFilter === "All Countries" || item.country === countryFilter) &&
      (searchQuery === "" ||
        item.projectName.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.organization.toLowerCase().includes(searchQuery.toLowerCase()))
    );
  });

  // Filter function for investor requests
  const filteredRequests = investorRequests.filter(item => {
    return (
      (sectorFilter === "All Sectors" || item.sector.includes(sectorFilter)) &&
      (searchQuery === "" ||
        item.projectName.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.organization.toLowerCase().includes(searchQuery.toLowerCase()))
    );
  });

  return (
    <Box sx={{ display: 'flex' }}>
      
      <MainContent isSidebarOpen={false}>
      
        <Container maxWidth="xl" sx={{ pt: 4, pb: 6 }}>
          {/* Hero section with gradient background */}
          <MotionPaper
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            elevation={0}
            sx={{
              mb: 6,
              p: { xs: 4, md: 6 },
              borderRadius: 3,
              background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
              boxShadow: `0 12px 48px ${alpha(theme.palette.primary.main, 0.25)}`,
              position: 'relative',
              overflow: 'hidden',
              color: 'white',
              textAlign: 'center'
            }}
          >
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                right: 0,
                width: '50%',
                height: '100%',
                opacity: 0.15,
                background: `radial-gradient(circle, ${theme.palette.common.white} 0%, transparent 70%)`,
                transform: 'translateX(30%)'
              }}
            />

            <Box
              sx={{
                position: 'relative',
                zIndex: 1,
                maxWidth: '800px',
                mx: 'auto'
              }}
            >
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
              >
                <Typography
                  variant="h2"
                  component="h1"
                  gutterBottom
                  sx={{
                    fontWeight: 700,
                    mb: 2,
                    textShadow: '0 4px 12px rgba(0,0,0,0.2)'
                  }}
                >
                  Opportunities Marketplace
                </Typography>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.3 }}
              >
                <Typography
                  variant="h5"
                  gutterBottom
                  sx={{
                    mb: 3,
                    fontWeight: 500,
                    opacity: 0.95
                  }}
                >
                  Discover, Connect, Invest: Saudi Arabia's Value Chain Opportunities Marketplace
                </Typography>
              </motion.div>
            </Box>
          </MotionPaper>

          {/* Value Proposition Section */}
          <Grid container spacing={4} sx={{ mb: 6 }}>
            <Grid item xs={12} md={6}>
              <MotionPaper
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
                whileHover={{ y: -8, scale: 1.02 }}
                elevation={2}
                sx={{
                  p: 4,
                  borderRadius: 3,
                  height: '100%',
                  position: 'relative',
                  overflow: 'hidden',
                  background: `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 1)}, ${alpha(theme.palette.primary.main, 0.03)})`,
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                  boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.1)}`,
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  '&:hover': {
                    boxShadow: `0 12px 40px ${alpha(theme.palette.primary.main, 0.2)}`,
                    border: `1px solid ${alpha(theme.palette.primary.main, 0.4)}`
                  }
                }}
              >
                <Box sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: 4,
                  height: '100%',
                  background: `linear-gradient(180deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`
                }} />

                <Box sx={{ pl: 2 }}>
                  <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Box
                      sx={{
                        p: 1.5,
                        borderRadius: 2,
                        background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.15)}, ${alpha(theme.palette.primary.light, 0.1)})`,
                        mr: 1.5,
                        display: 'flex',
                        alignItems: 'center'
                      }}
                    >
                      <StoreIcon sx={{ color: theme.palette.primary.main }} />
                    </Box>
                    For Project Owners
                  </Typography>

                  <Typography variant="body1" paragraph sx={{ lineHeight: 1.7, mb: 3 }}>
                    Showcase your project to Global and Saudi Investors and Partners – secure funding, expertise, and growth in Saudi Arabia's booming industries.
                  </Typography>

                  <Button
                    variant="contained"
                    color="primary"
                    sx={{
                      mt: 2,
                      textTransform: 'none',
                      borderRadius: 2,
                      px: 4,
                      py: 1.2,
                      background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                      boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`,
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: `0 6px 20px ${alpha(theme.palette.primary.main, 0.4)}`
                      }
                    }}
                    onClick={() => navigate('/marketplace/submit-opportunity')}
                  >
                    Submit Your Project
                  </Button>
                </Box>
              </MotionPaper>
            </Grid>

            <Grid item xs={12} md={6}>
              <MotionPaper
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
                whileHover={{ y: -8, scale: 1.02 }}
                elevation={2}
                sx={{
                  p: 4,
                  borderRadius: 3,
                  height: '100%',
                  position: 'relative',
                  overflow: 'hidden',
                  background: `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 1)}, ${alpha(theme.palette.secondary.main, 0.03)})`,
                  border: `1px solid ${alpha(theme.palette.secondary.main, 0.2)}`,
                  boxShadow: `0 4px 20px ${alpha(theme.palette.secondary.main, 0.1)}`,
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  '&:hover': {
                    boxShadow: `0 12px 40px ${alpha(theme.palette.secondary.main, 0.2)}`,
                    border: `1px solid ${alpha(theme.palette.secondary.main, 0.4)}`
                  }
                }}
              >
                <Box sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: 4,
                  height: '100%',
                  background: `linear-gradient(180deg, ${theme.palette.secondary.main}, ${theme.palette.secondary.light})`
                }} />

                <Box sx={{ pl: 2 }}>
                  <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Box
                      sx={{
                        p: 1.5,
                        borderRadius: 2,
                        background: `linear-gradient(135deg, ${alpha(theme.palette.secondary.main, 0.15)}, ${alpha(theme.palette.secondary.light, 0.1)})`,
                        mr: 1.5,
                        display: 'flex',
                        alignItems: 'center'
                      }}
                    >
                      <InvestorIcon sx={{ color: theme.palette.secondary.main }} />
                    </Box>
                    For Investors
                  </Typography>

                  <Typography variant="body1" paragraph sx={{ lineHeight: 1.7, mb: 3 }}>
                    Find high-potential projects, describe the project you want to fund/collaborate on across Saudi Arabia's key sectors – invest in the future of the region's economy.
                  </Typography>

                  <Button
                    variant="contained"
                    color="secondary"
                    sx={{
                      mt: 2,
                      textTransform: 'none',
                      borderRadius: 2,
                      px: 4,
                      py: 1.2,
                      background: `linear-gradient(135deg, ${theme.palette.secondary.main}, ${theme.palette.secondary.dark})`,
                      boxShadow: `0 4px 12px ${alpha(theme.palette.secondary.main, 0.3)}`,
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: `0 6px 20px ${alpha(theme.palette.secondary.main, 0.4)}`
                      }
                    }}
                    onClick={() => navigate('/marketplace/submit-request')}
                  >
                    Submit Your Request
                  </Button>
                </Box>
              </MotionPaper>
            </Grid>
          </Grid>
          
          <Typography variant="body1" sx={{ mb: 4, textAlign: 'center', fontStyle: 'italic' }}>
            Together, we drive growth and innovation towards a smart economy in alignment with Saudi, regional, and global goals, aiming that all stakeholders benefit.
          </Typography>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <Typography
              variant="h5"
              sx={{
                mb: 4,
                textAlign: 'center',
                fontWeight: 600,
                background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent'
              }}
            >
              Find investment opportunities placed by project owners and find project requests placed by investors
            </Typography>
          </motion.div>

          {/* Tabs for Investment Opportunities and Investor Requests */}
          <MotionPaper
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            elevation={2}
            sx={{
              borderRadius: 3,
              overflow: 'hidden',
              mb: 6,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
              boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`
            }}
          >
            <Box
              sx={{
                position: 'relative',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  height: '3px',
                  background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`
                }
              }}
            >
              <Tabs
                value={tabValue}
                onChange={handleTabChange}
                textColor="primary"
                indicatorColor="primary"
                variant="fullWidth"
                sx={{
                  borderBottom: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
                  background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.03)}, ${alpha(theme.palette.secondary.main, 0.02)})`,
                  '& .MuiTab-root': {
                    minHeight: 64,
                    px: 3,
                    fontWeight: 500,
                    textTransform: 'none',
                    fontSize: '1rem',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      background: alpha(theme.palette.primary.main, 0.08),
                      transform: 'translateY(-2px)'
                    },
                    '&.Mui-selected': {
                      background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.12)}, ${alpha(theme.palette.secondary.main, 0.08)})`,
                      color: theme.palette.primary.main
                    }
                  },
                  '& .MuiTabs-indicator': {
                    height: 3,
                    borderRadius: '3px 3px 0 0',
                    background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`
                  }
                }}
              >
                <Tab
                  label="Investment Opportunities"
                  icon={<HandshakeIcon />}
                  iconPosition="start"
                />
                <Tab
                  label="Investor's Request"
                  icon={<FundingIcon />}
                  iconPosition="start"
                />
              </Tabs>
            </Box>

            <Box sx={{ p: 4 }}>
              {/* Search and Filter Bar */}
              <Grid container spacing={2} sx={{ mb: 4 }}>
                <Grid item xs={12} md={6}>
                  <SearchBar>
                    <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
                    <InputBase
                      placeholder="Search by project name or organization..."
                      fullWidth
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                    />
                  </SearchBar>
                </Grid>
                <Grid item xs={12} md={3}>
                  <FormControl fullWidth variant="outlined" size="small" sx={{ borderRadius: theme.spacing(2) }}>
                    <InputLabel>Sector</InputLabel>
                    <Select
                      value={sectorFilter}
                      onChange={(e) => setSectorFilter(e.target.value)}
                      label="Sector"
                      startAdornment={
                        <InputAdornment position="start">
                          <BusinessIcon fontSize="small" />
                        </InputAdornment>
                      }
                    >
                      {sectors.map((sector) => (
                        <MenuItem key={sector} value={sector}>{sector}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={3}>
                  <FormControl fullWidth variant="outlined" size="small" sx={{ borderRadius: theme.spacing(2) }}>
                    <InputLabel>Country</InputLabel>
                    <Select
                      value={countryFilter}
                      onChange={(e) => setCountryFilter(e.target.value)}
                      label="Country"
                      startAdornment={
                        <InputAdornment position="start">
                          <LocationIcon fontSize="small" />
                        </InputAdornment>
                      }
                      disabled={tabValue === 1} // Disable for investor requests tab
                    >
                      {countries.map((country) => (
                        <MenuItem key={country} value={country}>{country}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>

              {/* Investment Opportunities Tab */}
              {tabValue === 0 && (
                <>
                  {loading ? (
                    <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center', py: 8 }}>
                      <CircularProgress />
                    </Box>
                  ) : (
                <Grid container spacing={3}>
                  {filteredOpportunities.length > 0 ? filteredOpportunities.map((opportunity, index) => (
                    <Grid item xs={12} sm={6} md={4} key={opportunity.id}>
                      <MotionCard
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.4, delay: index * 0.1 }}
                        whileHover={{ y: -12, scale: 1.02 }}
                        elevation={2}
                        sx={{
                          borderRadius: 3,
                          height: '100%',
                          background: `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 1)}, ${alpha(theme.palette.primary.main, 0.02)})`,
                          border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
                          boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.1)}`,
                          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                          '&:hover': {
                            boxShadow: `0 12px 40px ${alpha(theme.palette.primary.main, 0.2)}`,
                            border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
                          }
                        }}
                      >
                        <CardActionArea
                          onClick={() => handleOpportunityClick(opportunity.id)}
                          sx={{
                            height: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'stretch',
                            position: 'relative',
                            '&:hover .company-logo': {
                              transform: 'scale(1.05)',
                            }
                          }}
                        >
                          {/* Subtle top accent */}
                          <Box
                            sx={{
                              height: 4,
                              width: '100%',
                              background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
                            }}
                          />

                          <CardContent sx={{ p: 3, flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                            {/* Logo and Title Section */}
                            <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2.5, gap: 2 }}>
                              <Avatar
                                className="company-logo"
                                src={`https://ui-avatars.com/api/?name=${encodeURIComponent(opportunity.organization)}&background=random&size=70&bold=true`}
                                alt={opportunity.organization}
                                sx={{
                                  width: 70,
                                  height: 70,
                                  border: `3px solid ${alpha(theme.palette.primary.main, 0.15)}`,
                                  boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.15)}`,
                                  flexShrink: 0,
                                  transition: 'transform 0.2s ease',
                                  background: alpha(theme.palette.background.paper, 0.8)
                                }}
                              />
                              <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                                <Typography
                                  variant="h6"
                                  sx={{
                                    fontWeight: 700,
                                    lineHeight: 1.3,
                                    mb: 0.5,
                                    color: theme.palette.text.primary,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    display: '-webkit-box',
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: 'vertical',
                                  }}
                                >
                                  {opportunity.projectName}
                                </Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  <BusinessIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    sx={{
                                      fontWeight: 500,
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      whiteSpace: 'nowrap'
                                    }}
                                  >
                                    {opportunity.organization}
                                  </Typography>
                                </Box>
                              </Box>
                            </Box>

                            {/* Sector Badge */}
                            <Box
                              sx={{
                                mb: 2,
                                p: 1.5,
                                borderRadius: 1.5,
                                bgcolor: alpha(theme.palette.primary.main, 0.05),
                                border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`
                              }}
                            >
                              <Typography
                                variant="body2"
                                sx={{
                                  fontWeight: 600,
                                  color: theme.palette.primary.main,
                                  fontSize: '0.875rem'
                                }}
                              >
                                {opportunity.sector}
                              </Typography>
                            </Box>

                            {/* Status Chips */}
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                              <Chip
                                label={opportunity.stage}
                                size="small"
                                color={
                                  opportunity.stage === "Ready-to-Build" ? "success" :
                                  opportunity.stage === "Feasibility" ? "primary" :
                                  "default"
                                }
                                sx={{
                                  fontWeight: 600,
                                  fontSize: '0.75rem'
                                }}
                              />
                              <Chip
                                icon={<LocationIcon fontSize="small" />}
                                label={opportunity.country}
                                size="small"
                                variant="outlined"
                                sx={{
                                  fontWeight: 500,
                                  borderColor: alpha(theme.palette.primary.main, 0.3)
                                }}
                              />
                            </Box>

                            <Box sx={{ flexGrow: 1 }} />

                            <Divider sx={{ mb: 2 }} />
                            
                            <Button
                              fullWidth
                              variant="contained"
                              color="primary"
                              endIcon={<ArrowForwardIcon />}
                              sx={{
                                textTransform: 'none',
                                borderRadius: 2,
                                fontWeight: 600,
                                py: 1.25,
                                background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                                boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`,
                                '&:hover': {
                                  background: `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
                                  boxShadow: `0 6px 16px ${alpha(theme.palette.primary.main, 0.4)}`,
                                  transform: 'translateY(-1px)'
                                },
                                transition: 'all 0.2s ease'
                              }}
                            >
                              View Details
                            </Button>
                          </CardContent>
                        </CardActionArea>
                      </MotionCard>
                    </Grid>
                  )) : (
                    <Box sx={{ width: '100%', py: 4, textAlign: 'center' }}>
                      <Typography variant="h6" color="text.secondary">
                        No opportunities match your filters
                      </Typography>
                      <Button 
                        variant="text" 
                        color="primary" 
                        onClick={() => {
                          setSectorFilter("All Sectors");
                          setCountryFilter("All Countries");
                          setSearchQuery("");
                        }}
                        sx={{ mt: 1 }}
                      >
                        Clear Filters
                      </Button>
                    </Box>
                  )}
                </Grid>
                  )}
                </>
              )}

              {/* Investor Requests Tab */}
              {tabValue === 1 && (
                <>
                  {loading ? (
                    <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center', py: 8 }}>
                      <CircularProgress />
                    </Box>
                  ) : (
                <Grid container spacing={3}>
                  {filteredRequests.length > 0 ? filteredRequests.map((request) => (
                    <Grid item xs={12} sm={6} md={4} key={request.id}>
                      <MotionCard
                        whileHover={{ y: -5 }}
                        elevation={2}
                        sx={{
                          borderRadius: 2,
                          height: '100%',
                          transition: 'all 0.3s ease',
                          '&:hover': {
                            boxShadow: '0 12px 20px -10px rgba(0,0,0,0.2)'
                          }
                        }}
                      >
                        <CardActionArea
                          onClick={() => handleRequestClick(request.id)}
                          sx={{
                            height: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'stretch',
                            position: 'relative',
                            '&:hover .company-logo': {
                              transform: 'scale(1.05)',
                            }
                          }}
                        >
                          {/* Subtle top accent */}
                          <Box
                            sx={{
                              height: 4,
                              width: '100%',
                              background: `linear-gradient(90deg, ${theme.palette.secondary.main}, ${theme.palette.secondary.light})`,
                            }}
                          />

                          <CardContent sx={{ p: 3, flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                            {/* Logo and Title Section */}
                            <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2.5, gap: 2 }}>
                              <Avatar
                                className="company-logo"
                                src={`https://ui-avatars.com/api/?name=${encodeURIComponent(request.organization)}&background=random&size=70&bold=true`}
                                alt={request.organization}
                                sx={{
                                  width: 70,
                                  height: 70,
                                  border: `3px solid ${alpha(theme.palette.secondary.main, 0.15)}`,
                                  boxShadow: `0 4px 12px ${alpha(theme.palette.secondary.main, 0.15)}`,
                                  flexShrink: 0,
                                  transition: 'transform 0.2s ease',
                                  background: alpha(theme.palette.background.paper, 0.8)
                                }}
                              />
                              <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                                <Typography
                                  variant="h6"
                                  sx={{
                                    fontWeight: 700,
                                    lineHeight: 1.3,
                                    mb: 0.5,
                                    color: theme.palette.text.primary,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    display: '-webkit-box',
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: 'vertical',
                                  }}
                                >
                                  {request.projectName}
                                </Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  <BusinessIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    sx={{
                                      fontWeight: 500,
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      whiteSpace: 'nowrap'
                                    }}
                                  >
                                    {request.organization}
                                  </Typography>
                                </Box>
                              </Box>
                            </Box>

                            {/* Sector Badge */}
                            <Box
                              sx={{
                                mb: 2,
                                p: 1.5,
                                borderRadius: 1.5,
                                bgcolor: alpha(theme.palette.secondary.main, 0.05),
                                border: `1px solid ${alpha(theme.palette.secondary.main, 0.1)}`
                              }}
                            >
                              <Typography
                                variant="body2"
                                sx={{
                                  fontWeight: 600,
                                  color: theme.palette.secondary.main,
                                  fontSize: '0.875rem'
                                }}
                              >
                                {request.sector}
                              </Typography>
                            </Box>

                            {/* Additional Details */}
                            {request.preferredIndustry && (
                              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                <BusinessIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                  sx={{
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap'
                                  }}
                                >
                                  {request.preferredIndustry}
                                </Typography>
                              </Box>
                            )}
                            {request.location && (
                              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                <LocationIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                  sx={{
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap'
                                  }}
                                >
                                  {request.location}
                                </Typography>
                              </Box>
                            )}

                            <Box sx={{ flexGrow: 1 }} />

                            <Divider sx={{ mb: 2 }} />

                            <Button
                              fullWidth
                              variant="contained"
                              color="secondary"
                              endIcon={<ArrowForwardIcon />}
                              sx={{
                                textTransform: 'none',
                                borderRadius: 2,
                                fontWeight: 600,
                                py: 1.25,
                                background: `linear-gradient(135deg, ${theme.palette.secondary.main}, ${theme.palette.secondary.dark})`,
                                boxShadow: `0 4px 12px ${alpha(theme.palette.secondary.main, 0.3)}`,
                                '&:hover': {
                                  background: `linear-gradient(135deg, ${theme.palette.secondary.dark}, ${theme.palette.secondary.main})`,
                                  boxShadow: `0 6px 16px ${alpha(theme.palette.secondary.main, 0.4)}`,
                                  transform: 'translateY(-1px)'
                                },
                                transition: 'all 0.2s ease'
                              }}
                            >
                              View Request
                            </Button>
                          </CardContent>
                        </CardActionArea>
                      </MotionCard>
                    </Grid>
                  )) : (
                    <Box sx={{ width: '100%', py: 4, textAlign: 'center' }}>
                      <Typography variant="h6" color="text.secondary">
                        No investor requests match your filters
                      </Typography>
                      <Button 
                        variant="text" 
                        color="primary" 
                        onClick={() => {
                          setSectorFilter("All Sectors");
                          setSearchQuery("");
                        }}
                        sx={{ mt: 1 }}
                      >
                        Clear Filters
                      </Button>
                    </Box>
                  )}
                </Grid>
                  )}
                </>
              )}
            </Box>
          </MotionPaper>

          {/* Call to Action */}
          <Paper
            elevation={0}
            sx={{
              mb: 6,
              p: { xs: 4, md: 6 },
              borderRadius: 3,
              background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.9)}, ${alpha(theme.palette.secondary.main, 0.8)})`,
              position: 'relative',
              overflow: 'hidden',
              color: 'white',
              textAlign: 'center',
            }}
          >
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                right: 0,
                width: '50%',
                height: '100%',
                opacity: 0.1,
                background: `radial-gradient(circle, ${theme.palette.common.white} 0%, transparent 70%)`,
                transform: 'translateX(30%)',
              }}
            />
            <Box sx={{ position: 'relative', zIndex: 1 }}>
              <Typography variant="h4" gutterBottom fontWeight={600}>
                Ready to Join Saudi Arabia's Investment Ecosystem?
              </Typography>

              <Typography variant="body1" sx={{ mb: 4, maxWidth: 800, mx: 'auto', opacity: 0.9 }}>
                Whether you're looking to fund your project or find the perfect investment opportunity, our marketplace connects you with the right partners.
              </Typography>

              <Box sx={{ display: 'flex', justifyContent: 'center', gap: 3 }}>
                <Button
                  variant="contained"
                  size="large"
                  onClick={() => navigate('/marketplace/submit-opportunity')}
                  sx={{
                    bgcolor: 'white',
                    color: theme.palette.primary.main,
                    '&:hover': {
                        bgcolor: alpha(theme.palette.common.white, 0.9)
                    },
                    fontWeight: 600,
                    borderRadius: 2,
                    px: 4
                  }}
                >
                  Submit a Project
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  onClick={() => navigate('/marketplace/submit-request')}
                  sx={{
                    color: 'white',
                    borderColor: 'white',
                    '&:hover': {
                      borderColor: 'white',
                      bgcolor: alpha(theme.palette.common.white, 0.1)
                    },
                    fontWeight: 600,
                    borderRadius: 2,
                    px: 4
                  }}
                >
                  Submit Investor Request
                </Button>
              </Box>
            </Box>
          </Paper>

          {/* FAQ Section */}
          <Box sx={{ mb: 6 }}>
            <SectionHeader>
              <Typography variant="h4" fontWeight={600} gutterBottom>
                Frequently Asked Questions
              </Typography>
            </SectionHeader>
            
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Paper elevation={1} sx={{ p: 3, borderRadius: 2 }}>
                  <Typography variant="h6" fontWeight={600} gutterBottom>
                    How do I submit my project?
                  </Typography>
                  <Typography variant="body2">
                    Click on "Submit Your Project" button and fill out the form with your project details. Our team will review your submission and get in touch within 48 hours to verify and publish your opportunity.
                  </Typography>
                </Paper>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Paper elevation={1} sx={{ p: 3, borderRadius: 2 }}>
                  <Typography variant="h6" fontWeight={600} gutterBottom>
                    Who can invest in these opportunities?
                  </Typography>
                  <Typography variant="body2">
                    Both Saudi and international investors can participate. Each project has its own specific requirements regarding minimum investment and investor qualifications, which will be detailed in the full opportunity view.
                  </Typography>
                </Paper>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Paper elevation={1} sx={{ p: 3, borderRadius: 2 }}>
                  <Typography variant="h6" fontWeight={600} gutterBottom>
                    How are projects vetted?
                  </Typography>
                  <Typography variant="body2">
                    All submitted projects undergo thorough verification by our team, including business plan review, legal status check, and confirmation of key claims. We maintain high standards to ensure quality opportunities.
                  </Typography>
                </Paper>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Paper elevation={1} sx={{ p: 3, borderRadius: 2 }}>
                  <Typography variant="h6" fontWeight={600} gutterBottom>
                    What happens after I express interest?
                  </Typography>
                  <Typography variant="body2">
                    When you express interest in a project, we facilitate an introduction between you and the project owner. Further discussions, due diligence, and investment terms are handled directly between the parties involved.
                  </Typography>
                </Paper>
              </Grid>
            </Grid>
          </Box>

          {/* Success Stories/Testimonials */}
          {featuredTestimonials.length > 0 && (
            <Box sx={{ mb: 6 }}>
              <SectionHeader>
                <Typography variant="h4" fontWeight={600} gutterBottom>
                  Success Stories
                </Typography>
              </SectionHeader>

              <Grid container spacing={4}>
                {featuredTestimonials.slice(0, 3).map((testimonial) => {
                  const user = testimonial.user || {};
                  const initials = `${user.firstName?.charAt(0) || ''}${user.lastName?.charAt(0) || ''}`.toUpperCase();

                  return (
                    <Grid item xs={12} md={4} key={testimonial.id}>
                      <Paper
                        elevation={2}
                        sx={{
                          p: 3,
                          borderRadius: 2,
                          height: '100%',
                          position: 'relative',
                          overflow: 'hidden'
                        }}
                      >
                        <Box
                          sx={{
                            position: 'absolute',
                            top: 0,
                            right: 0,
                            width: 100,
                            height: 100,
                            bgcolor: alpha(theme.palette.primary.light, 0.1),
                            borderRadius: '0 0 0 100%'
                          }}
                        />
                        <Typography
                          variant="h6"
                          fontWeight={600}
                          gutterBottom
                          sx={{ color: theme.palette.primary.main }}
                        >
                          {testimonial.title}
                        </Typography>

                        <Typography variant="body2" paragraph>
                          "{testimonial.content}"
                        </Typography>

                        <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
                          <Avatar
                            src={user.photoUrl}
                            sx={{ bgcolor: theme.palette.primary.main, mr: 2 }}
                          >
                            {initials}
                          </Avatar>
                          <Box>
                            <Typography variant="subtitle2" fontWeight={600}>
                              {user.firstName} {user.lastName}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {user.organizationName || user.title}
                            </Typography>
                          </Box>
                        </Box>
                      </Paper>
                    </Grid>
                  );
                })}
              </Grid>
            </Box>
          )}
        </Container>
      </MainContent>
    </Box>
  );
};

export default MarketplacePage;